'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Loader2, Mail, ShieldCheck, Eye, EyeOff } from 'lucide-react';

// ─── პაროლის სიძლიერე ────────────────────────────────────────────────────────
function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score: 1, label: 'სუსტი', color: 'bg-red-500' };
  if (score <= 3) return { score: 2, label: 'საშუალო', color: 'bg-yellow-400' };
  return { score: 3, label: 'ძლიერი', color: 'bg-emerald-500' };
}

// ─── Rate limiting ────────────────────────────────────────────────────────────
const RATE_KEY = 'unirate_failed_attempts';
const MAX_ATTEMPTS = 5;
const BLOCK_DURATION_MS = 5 * 60 * 1000;

function getRateData(): { attempts: number; blockedUntil: number } {
  try {
    const raw = localStorage.getItem(RATE_KEY);
    if (!raw) return { attempts: 0, blockedUntil: 0 };
    return JSON.parse(raw);
  } catch {
    return { attempts: 0, blockedUntil: 0 };
  }
}

function recordFailedAttempt() {
  const data = getRateData();
  const attempts = data.attempts + 1;
  const blockedUntil = attempts >= MAX_ATTEMPTS ? Date.now() + BLOCK_DURATION_MS : data.blockedUntil;
  localStorage.setItem(RATE_KEY, JSON.stringify({ attempts, blockedUntil }));
  return { attempts, blockedUntil };
}

function clearRateData() {
  localStorage.removeItem(RATE_KEY);
}

function isBlocked(): { blocked: boolean; remainingMs: number } {
  const { blockedUntil } = getRateData();
  const remaining = blockedUntil - Date.now();
  return { blocked: remaining > 0, remainingMs: Math.max(0, remaining) };
}

// ─── Loading Skeleton ─────────────────────────────────────────────────────────
function LoginSkeleton() {
  return (
    <div className="bg-card p-8 md:p-10 flex flex-col justify-center gap-5 animate-pulse">
      <div>
        <div className="h-7 w-48 bg-muted rounded-lg mb-2" />
        <div className="h-4 w-64 bg-muted rounded-lg" />
      </div>
      <div className="flex rounded-xl border border-border overflow-hidden">
        <div className="flex-1 h-10 bg-primary/20" />
        <div className="flex-1 h-10 bg-muted" />
      </div>
      <div className="flex flex-col gap-3">
        <div className="h-4 w-16 bg-muted rounded" />
        <div className="h-12 w-full bg-muted rounded-xl" />
        <div className="h-4 w-16 bg-muted rounded" />
        <div className="h-12 w-full bg-muted rounded-xl" />
        <div className="h-12 w-full bg-primary/30 rounded-xl mt-1" />
      </div>
      <div className="h-16 w-full bg-muted rounded-xl" />
    </div>
  );
}

// ─── მთავარი კომპონენტი ───────────────────────────────────────────────────────
export default function LoginCard() {
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [blockTimer, setBlockTimer] = useState(0);
  const blockInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const { signIn, signUp, user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const passwordStrength = getPasswordStrength(password);

  useEffect(() => {
    const t = setTimeout(() => setPageLoading(false), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const errorParam = searchParams?.get('error');
    if (errorParam === 'domain_restricted') {
      setError('Access denied. Only @kiu.edu.ge Microsoft accounts are permitted.');
    } else if (errorParam === 'auth_failed') {
      setError('Authentication failed. Please try again.');
    } else if (errorParam === 'session_expired') {
      setError('სესია ამოიწურა. გთხოვთ ხელახლა შეხვიდეთ.');
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) router.replace('/');
  }, [user, router]);

  useEffect(() => {
    const { blocked, remainingMs } = isBlocked();
    if (blocked) {
      setBlockTimer(Math.ceil(remainingMs / 1000));
      blockInterval.current = setInterval(() => {
        const { blocked: stillBlocked, remainingMs: rem } = isBlocked();
        if (!stillBlocked) {
          clearRateData();
          setBlockTimer(0);
          if (blockInterval.current) clearInterval(blockInterval.current);
        } else {
          setBlockTimer(Math.ceil(rem / 1000));
        }
      }, 1000);
    }
    return () => { if (blockInterval.current) clearInterval(blockInterval.current); };
  }, []);

  function handleEmailChange(val: string) {
    setEmail(val);
    setShowEmailSuggestion(val.includes('@') && !val.includes('@kiu.edu.ge'));
  }

  function applySuggestion() {
    const base = email.split('@')[0];
    setEmail(`${base}@kiu.edu.ge`);
    setShowEmailSuggestion(false);
  }

  async function handleEmailAuth(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const { blocked, remainingMs } = isBlocked();
    if (blocked) {
      setError(`ძალიან ბევრი მცდელობა. სცადე ${Math.ceil(remainingMs / 1000)} წამში.`);
      return;
    }
    if (mode === 'signup' && password !== confirmPassword) {
      setError('პაროლები არ ემთხვევა. სცადე კიდევ.');
      return;
    }
    if (mode === 'signup' && !termsAccepted) {
      setError('გთხოვთ დაეთანხმოთ წესებსა და პირობებს.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
        clearRateData();
        router.replace('/');
      } else {
        await signUp(email, password);
        setOtpStep(true);
        setSuccess('კოდი გამოგეგზავნა ემეილზე. შეიყვანე ქვემოთ.');
        setLoading(false);
      }
    } catch (err: any) {
      setLoading(false);
      if (mode === 'signin') {
        const { attempts, blockedUntil } = recordFailedAttempt();
        if (blockedUntil > Date.now()) {
          const rem = Math.ceil((blockedUntil - Date.now()) / 1000);
          setBlockTimer(rem);
          setError(`ძალიან ბევრი მცდელობა. სცადე ${rem} წამში.`);
          blockInterval.current = setInterval(() => {
            const { blocked: stillBlocked, remainingMs: rem } = isBlocked();
            if (!stillBlocked) {
              clearRateData();
              setBlockTimer(0);
              if (blockInterval.current) clearInterval(blockInterval.current);
            } else {
              setBlockTimer(Math.ceil(rem / 1000));
            }
          }, 1000);
        } else {
          const left = MAX_ATTEMPTS - attempts;
          setError(`${err?.message || 'შეცდომა.'} ${left > 0 ? `კიდევ ${left} მცდელობა დარჩა.` : ''}`);
        }
      } else {
        setError(err?.message || 'შეცდომა. სცადე კიდევ.');
      }
    }
  }

  async function handleOtpVerify(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.auth.verifyOtp({ email, token: otp, type: 'signup' });
      if (error) throw error;
      router.replace('/');
    } catch (err: any) {
      setLoading(false);
      setError(err?.message || 'კოდი არასწორია. სცადე კიდევ.');
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setForgotSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'შეცდომა. სცადე კიდევ.');
      setShowForgotModal(false);
    } finally {
      setForgotLoading(false);
    }
  }

  if (pageLoading) return <LoginSkeleton />;

  return (
    <div className="bg-card p-8 md:p-10 flex flex-col justify-center gap-5">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Sign in to UniRate</h1>
        <p className="text-sm text-muted-foreground">Use your university account to continue.</p>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
          <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
          <Mail size={16} className="text-green-600 shrink-0 mt-0.5" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {otpStep ? (
        <form onSubmit={handleOtpVerify} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">დადასტურების კოდი</label>
            <input
              type="text"
              required
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-ნიშნა კოდი"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 tracking-widest text-center text-lg"
              maxLength={6}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
            {loading ? 'მიმდინარეობს...' : 'დადასტურება'}
          </button>
        </form>
      ) : (
        <>
          <div className="flex rounded-xl border border-border overflow-hidden">
            <button
              onClick={() => { setMode('signin'); setError(''); setSuccess(''); setConfirmPassword(''); setTermsAccepted(false); setPassword(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mode === 'signin' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}
            >
              შესვლა
            </button>
            <button
              onClick={() => { setMode('signup'); setError(''); setSuccess(''); setPassword(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mode === 'signup' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}
            >
              რეგისტრაცია
            </button>
          </div>

          <form onSubmit={handleEmailAuth} className="flex flex-col gap-3">

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">ემეილი</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="example@kiu.edu.ge"
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              {showEmailSuggestion && (
                <button
                  type="button"
                  onClick={applySuggestion}
                  className="self-start text-xs text-primary hover:underline"
                >
                  გამოიყენე <span className="font-semibold">{email.split('@')[0]}@kiu.edu.ge</span>?
                </button>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">პაროლი</label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => { setShowForgotModal(true); setForgotEmail(email); setForgotSuccess(false); }}
                    className="text-xs text-primary hover:underline"
                  >
                    დაგავიწყდა?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="მინიმუმ 6 სიმბოლო"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {mode === 'signup' && password.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-1">
                  <div className="flex gap-1">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= passwordStrength.score ? passwordStrength.color : 'bg-muted'}`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${
                    passwordStrength.score === 1 ? 'text-red-500' :
                    passwordStrength.score === 2 ? 'text-yellow-500' : 'text-emerald-500'
                  }`}>
                    {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm password */}
            {mode === 'signup' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">გაიმეორე პაროლი</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="პაროლი კიდევ ერთხელ"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* Terms */}
            {mode === 'signup' && (
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-primary/40 cursor-pointer shrink-0"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  ვეთანხმები{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowTermsModal(true); }}
                    className="text-primary font-medium hover:underline"
                  >
                    წესებსა და პირობებს
                  </button>
                </span>
              </label>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || blockTimer > 0}
              className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : null}
              {loading
                ? 'მიმდინარეობს...'
                : blockTimer > 0
                ? `დაბლოკილია ${blockTimer}წმ`
                : mode === 'signin' ? 'შესვლა' : 'რეგისტრაცია'}
            </button>
          </form>

          <div className="flex items-start gap-3 p-4 rounded-xl bg-muted border border-border">
            <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-foreground">Secure Authentication</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                შენი მონაცემები უსაფრთხოდ ინახება. UniRate არასდროს ინახავს პაროლს.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => { setShowForgotModal(false); setForgotSuccess(false); }}>
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-base font-bold text-foreground">პაროლის აღდგენა</h2>
              <button onClick={() => { setShowForgotModal(false); setForgotSuccess(false); }} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-5">
              {forgotSuccess ? (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Mail size={22} className="text-green-600" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">ლინკი გამოგეგზავნა!</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    შეამოწმე <span className="font-medium text-foreground">{forgotEmail}</span> — პაროლის აღდგენის ლინკი გამოგეგზავნა.
                  </p>
                  <button onClick={() => { setShowForgotModal(false); setForgotSuccess(false); }} className="mt-2 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity">დახურვა</button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">შეიყვანე შენი ემეილი და გამოგიგზავნით პაროლის აღდგენის ლინკს.</p>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">ემეილი</label>
                    <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="example@kiu.edu.ge" className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                  </div>
                  <button type="submit" disabled={forgotLoading} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed">
                    {forgotLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {forgotLoading ? 'იგზავნება...' : 'ლინკის გაგზავნა'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTermsModal(false)}>
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-primary" />
                <h2 className="text-base font-bold text-foreground">Terms and Conditions</h2>
              </div>
              <button onClick={() => setShowTermsModal(false)} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto px-6 py-5 flex flex-col gap-3">
              {[
                'Users may only post reviews and ratings based on their real personal experience with a professor, assistant, or course.',
                'Fake reviews, misleading information, spam, or intentionally harmful comments are strictly prohibited.',
                'All users must communicate respectfully. Offensive language, harassment, hate speech, bullying, or personal attacks are not allowed.',
                'Sharing private or confidential information, including phone numbers, emails, student IDs, or personal details of others, is prohibited.',
                'UniRate reserves the right to remove any review or comment that violates these rules or is considered inappropriate.',
                'Users who repeatedly violate the rules may have their content deleted, accounts temporarily suspended, or permanently blocked from the platform.',
                'By using UniRate, you agree to follow these Terms and Conditions and help maintain a respectful and trustworthy student community.',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3 p-3.5 rounded-xl bg-muted border border-border">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <p className="text-sm text-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-border">
              <button onClick={() => { setTermsAccepted(true); setShowTermsModal(false); }} className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity">
                ვეთანხმები
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}